import { x } from '@xstyled/styled-components';
import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { t } from 'ttag';
import { select } from '~/lib/select';
import { Bar } from '~/training/progress-bar/ProgressBar.styles';
import { usePhoneAudioEffect } from '~/ui/audio-effect/usePhoneAudioEffect';
import { Flex } from '~/ui/flex/Flex';
import { Tooltip } from '~/ui/tooltip/Tooltip';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { shallow } from 'zustand/shallow';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { TrainingState, useTrainingStore } from '~/training/state';
import { EditorMediaFile, useEditorStore } from '~/shell/Store';
import FilePickerV2 from '~/editor/filePicker/FilePicker_v2';
import Avatar from './avatar.png';
import * as S from './styles';
import {
  getPhoneFitScale,
  PHONE_FRAME_HEIGHT_PX,
  PHONE_FRAME_WIDTH_PX
} from './phoneReflowLayout';

const selectorTraining = (s: TrainingState) => select(s, 'logoUrl');
const selector = (s: MediaPlayerStore) =>
  select(s, 'pause', 'play', 'progress', 'status', 'reset', 'loadMedia');

interface Props {
  id?: string;
  children: React.ReactNode;
  narration: string;
  subtitlesUrl?: string;
  autoPlay: boolean;
  showProgress?: boolean;
  name?: string;
  photo?: string;
  personalization?: boolean;
}

export const PhoneCall = ({
  id,
  children,
  narration,
  subtitlesUrl,
  showProgress,
  name,
  photo,
  personalization = false
}: Props) => {
  const avatarRef = useRef<HTMLDivElement | null>(null);
  const { logoUrl } = useTrainingStore(selectorTraining, shallow);
  const { contentEditable, editorMode, updateFile } = useEditorStore();
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [phoneScale, setPhoneScale] = useState(() => getPhoneFitScale());

  const [hasStarted, setHasStarted] = useState(false);
  const startTimeoutRef = useRef<number | null>(null);
  const audioEffectPlayedRef = useRef<boolean>(false);
  const { play, pause, progress, status, loadMedia } = useMediaPlayerStore(
    selector,
    shallow
  );

  const {
    audioEffectDuration,
    phoneAudioEffectIsPaused,
    pausePhoneAudioEffect
  } = usePhoneAudioEffect(editorMode);

  useGlobalMedia();

  // CAT-1229 / WCAG 1.4.10: shrink the fixed 345px frame so it fits ≤320 CSS px.
  useLayoutEffect(() => {
    const updateScale = () =>
      setPhoneScale(getPhoneFitScale(window.innerWidth));
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  useEffect(() => {
    pause();
  }, []);

  useEffect(() => {
    // Use setTimeout(0) to avoid audio loading issues during slide transitions.
    // This pushes loadMedia to the next event loop cycle, allowing React cleanup
    // from previous slides to complete before loading new media
    setTimeout(() => {
      loadMedia(narration, subtitlesUrl, 'audio', true, 0);
    }, 0);
  }, [narration, subtitlesUrl]);

  useEffect(() => {
    if (hasStarted) {
      pausePhoneAudioEffect();
    }
  }, [hasStarted]);

  useEffect(() => {
    // Pause only if playing started automatically (autoplay), not by user click
    if (status === 'playing' && !audioEffectPlayedRef.current && !hasStarted) {
      audioEffectPlayedRef.current = true;
      pause();
    }

    if (
      startTimeoutRef.current === null &&
      !phoneAudioEffectIsPaused &&
      !hasStarted &&
      status === 'paused'
    ) {
      startTimeoutRef.current = window.setTimeout(() => {
        setHasStarted(true);
        play();
      }, audioEffectDuration);
    }

    return () => {
      if (status === 'paused') {
        setHasStarted(true);
      }

      if (startTimeoutRef.current !== null) {
        clearTimeout(startTimeoutRef.current as number);
        startTimeoutRef.current = null;
      }
    };
  }, [status]);

  const UpdateFileElement = async (file: EditorMediaFile) => {
    if (id) {
      updateFile(id, file, 'PHONE');
    }
    setShowFilePicker(false);
  };

  return (
    <Flex h="100%" alignItems="center" justifyContent="center">
      {/*
        Layout box matches the *visual* size after scale — transform alone would
        leave a 345px flow width and still force horizontal scroll.
      */}
      <x.div
        position="relative"
        style={{
          width: PHONE_FRAME_WIDTH_PX * phoneScale,
          height: PHONE_FRAME_HEIGHT_PX * phoneScale
        }}
      >
        <S.Phone
          style={
            phoneScale < 1
              ? {
                  transform: `scale(${phoneScale})`,
                  transformOrigin: 'top left'
                }
              : undefined
          }
        >
          <Flex marginLeft="20px;">
            <div
              ref={avatarRef}
              style={{ display: 'inline-block', position: 'relative' }}
            >
              {photo || (personalization && logoUrl) ? (
                <x.img
                  src={photo || logoUrl}
                  w="33px"
                  h="33px"
                  borderRadius="50px"
                  backgroundColor="#FFF"
                  alt={''}
                  onClick={
                    contentEditable ? () => setShowFilePicker(true) : undefined
                  }
                  style={
                    contentEditable
                      ? { cursor: 'pointer', objectFit: 'contain' }
                      : { objectFit: 'contain' }
                  }
                />
              ) : (
                <x.img
                  src={Avatar}
                  w="33px"
                  h="33px"
                  borderRadius="50px"
                  backgroundColor="#FFF"
                  alt={''}
                  onClick={
                    contentEditable ? () => setShowFilePicker(true) : undefined
                  }
                  style={contentEditable ? { cursor: 'pointer' } : undefined}
                />
              )}

              {showFilePicker &&
                id &&
                createPortal(
                  <FilePickerV2
                    fileElRef={avatarRef}
                    updateFile={UpdateFileElement}
                    type="image"
                    onClose={() => setShowFilePicker(false)}
                  />,
                  document.body
                )}
            </div>

            <x.div margin="-3px 0 0 8px;">
              <S.UserName>
                {(
                  children as Array<Array<{ props?: { value?: unknown } }>>
                )[0][0].props?.value
                  ? children
                  : name}
              </S.UserName>
              <S.UserLabel>{t`Connecting...`}</S.UserLabel>
            </x.div>
          </Flex>

          {/* `aria-labelledby` expects element ids (not text) and `tabindex > 0`
              breaks the natural tab order, so the button relies on its
              `aria-label` + `title` and the default button focusability. */}
          <S.CallButton
            aria-label={t`Accept`}
            title={t`Click "Accept" to answer the call`}
            onClick={() => {
              if (!hasStarted) {
                audioEffectPlayedRef.current = true; // Prevent pause() in useEffect
                play();
                setHasStarted(true);
              }
            }}
          />

          {!hasStarted && !editorMode && (
            <Tooltip
              top="350px"
              // CAT-1229: on narrow/scaled frames nudge right so chrome (menu /
              // PREVIEW MODE) does not cover the first words.
              right={phoneScale < 1 ? '40px' : '73px'}
              placement={'bottom-right'}
            >
              {t`Click "Accept" to answer the call`}
            </Tooltip>
          )}
          {showProgress && (
            <S.ProgressWrapper>
              <Bar bg="greyDark">
                <Bar bg="white" w={`${progress * 100}%`} />
              </Bar>
            </S.ProgressWrapper>
          )}
        </S.Phone>
      </x.div>

      <MediaCaptions />
    </Flex>
  );
};
